interface ChatMetrics {
  totalConversations: number;
  averageResponseTime: number;
  messageCount: number;
  userSatisfaction: number;
  commonQueries: { [key: string]: number };
  responseErrors: number;
}

interface ChatInteraction {
  timestamp: number;
  userMessage: string;
  botResponse: string;
  responseTime: number;
  isError: boolean;
}

class ChatAnalytics {
  private static instance: ChatAnalytics;
  private metrics: ChatMetrics;
  private interactions: ChatInteraction[];
  private readonly STORAGE_KEY = 'chat_analytics_data';

  private constructor() {
    // Intentar cargar datos del localStorage
    const savedData = localStorage.getItem(this.STORAGE_KEY);
    if (savedData) {
      const { metrics, interactions } = JSON.parse(savedData);
      this.metrics = metrics;
      this.interactions = interactions;
    } else {
      this.metrics = {
        totalConversations: 0,
        averageResponseTime: 0,
        messageCount: 0,
        userSatisfaction: 0,
        commonQueries: {},
        responseErrors: 0
      };
      this.interactions = [];
    }
  }

  private saveToStorage(): void {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify({
      metrics: this.metrics,
      interactions: this.interactions
    }));
  }

  public static getInstance(): ChatAnalytics {
    if (!ChatAnalytics.instance) {
      ChatAnalytics.instance = new ChatAnalytics();
    }
    return ChatAnalytics.instance;
  }

  public logInteraction(interaction: ChatInteraction): void {
    this.interactions.push(interaction);
    this.updateMetrics(interaction);
    this.saveToStorage();
  }

  private updateMetrics(interaction: ChatInteraction): void {
    // Actualizar conteo de mensajes
    this.metrics.messageCount++;

    // Actualizar tiempo promedio de respuesta
    const totalTime = this.metrics.averageResponseTime * (this.metrics.messageCount - 1) + interaction.responseTime;
    this.metrics.averageResponseTime = totalTime / this.metrics.messageCount;

    // Actualizar queries comunes
    const query = interaction.userMessage.toLowerCase();
    this.metrics.commonQueries[query] = (this.metrics.commonQueries[query] || 0) + 1;

    // Actualizar errores
    if (interaction.isError) {
      this.metrics.responseErrors++;
    }

    // Actualizar conversaciones totales
    if (this.interactions.length === 1 || 
        this.interactions[this.interactions.length - 2]?.timestamp < interaction.timestamp - 300000) {
      this.metrics.totalConversations++;
    }

    this.saveToStorage();
  }

  public getMetrics(): ChatMetrics {
    return { ...this.metrics };
  }

  public getRecentInteractions(limit: number = 10): ChatInteraction[] {
    return [...this.interactions].slice(-limit);
  }

  public getCommonQueries(limit: number = 5): { query: string; count: number }[] {
    return Object.entries(this.metrics.commonQueries)
      .sort(([, a], [, b]) => b - a)
      .slice(0, limit)
      .map(([query, count]) => ({ query, count }));
  }

  public getErrorRate(): number {
    return this.metrics.messageCount > 0 
      ? (this.metrics.responseErrors / this.metrics.messageCount) * 100 
      : 0;
  }

  public clearData(): void {
    this.metrics = {
      totalConversations: 0,
      averageResponseTime: 0,
      messageCount: 0,
      userSatisfaction: 0,
      commonQueries: {},
      responseErrors: 0
    };
    this.interactions = [];
    this.saveToStorage();
  }
}

export const chatAnalytics = ChatAnalytics.getInstance(); 